import { useState } from 'react'
import { api } from '@/lib/api'
import { useStateWorkspaces } from '@/lib/hooks'
import { randomTokenName } from '@/features/tokens/lib/random'
import { CopyBlock } from '@/shared/components/CopyBlock'
import { Modal } from '@/shared/components/Modal'

interface NewSecret {
  client_id: string
  client_secret: string
}

interface Props {
  orgId: string
  orgName: string
  onClose: () => void
  onCreated: () => void
}

type Perm = 'none' | 'read' | 'write'

export function CreateTokenModal({ orgId, orgName, onClose, onCreated }: Props) {
  const [name, setName] = useState(randomTokenName)
  const [description, setDesc] = useState('')
  const [isGlobal, setIsGlobal] = useState(true)
  const [orgEnabled, setOrgEnabled] = useState(false)
  const [stateEnabled, setStateEnabled] = useState(false)
  const [stateGrants, setStateGrants] = useState<Map<string, Perm>>(new Map())
  const [errors, setErrors] = useState<{ name?: string; description?: string; grants?: string }>({})
  const [busy, setBusy] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const [secret, setSecret] = useState<NewSecret | null>(null)
  const [confirmedSaved, setConfirmedSaved] = useState(false)

  function handleGlobalToggle() {
    setIsGlobal(true)
    setOrgEnabled(false)
    setStateEnabled(false)
  }

  function handleOrgToggle() {
    const next = !orgEnabled
    setOrgEnabled(next)
    if (next) setIsGlobal(false)
    else if (!stateEnabled) setIsGlobal(true)
  }

  function handleStateToggle() {
    const next = !stateEnabled
    setStateEnabled(next)
    if (next) setIsGlobal(false)
    else if (!orgEnabled) setIsGlobal(true)
  }

  function toggleStateGrant(workspaceId: string) {
    setStateGrants((prev) => {
      const next = new Map(prev)
      if (next.has(workspaceId)) next.delete(workspaceId)
      else next.set(workspaceId, 'write')
      return next
    })
  }

  function setStatePerm(workspaceId: string, perm: Perm) {
    setStateGrants((prev) => new Map(prev).set(workspaceId, perm))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const errs: typeof errors = {}
    if (!name.trim()) errs.name = 'Name is required.'
    else if (/\s/.test(name)) errs.name = 'No spaces allowed.'
    if (description.length > 200) errs.description = 'Max 200 characters.'
    if (!isGlobal && stateEnabled && stateGrants.size === 0) errs.grants = 'Select at least one workspace.'
    if (Object.keys(errs).length) { setErrors(errs); return }

    setBusy(true)
    setApiError(null)
    try {
      // The "Global" option means full access within THIS org/tenant - not
      // app-wide. That maps to an org-scoped token with write on modules + state.
      // (Org modules / State workspaces granularity is "coming soon".)
      let scope: string
      if (stateEnabled) {
        scope = 'state_workspaces'
      } else {
        scope = 'org'
      }

      const body: Record<string, unknown> = { name: name.trim(), description, scope }
      if (scope === 'org') {
        body.include_modules = 'write'
        body.include_state = 'write'
      }
      if (stateEnabled) {
        body.state_grants = Array.from(stateGrants.entries()).map(([workspace_id, permission]) => ({ workspace_id, permission }))
      }

      const res = await api.post<{ id: string; oidc_client_id: string; client_secret: string }>(
        `/api/orgs/${orgId}/tokens`,
        body,
      )
      setSecret({ client_id: res.oidc_client_id, client_secret: res.client_secret })
      onCreated()
    } catch (err) {
      setApiError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  // The credentials are only ever in hand here, so offer them in the shape the CLI
  // wants - it auto-detects `client` mode from these two variables. One `export`
  // with both operands: CopyBlock is a single-line box and collapses newlines.
  const cliBotEnv = secret
    ? `export TERRAMANTLE_CLIENT_ID=${secret.client_id} TERRAMANTLE_CLIENT_SECRET=${secret.client_secret}`
    : ''

  return (
    <Modal
      track="create_token"
      title="New bot token"
      kicker={`// ${orgName}`}
      onClose={onClose}
      maxWidth={540}
      dismissable={!secret}
    >
      {secret ? (
        <div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.82rem', color: 'var(--text2)', marginBottom: 16 }}>
              Token <strong style={{ color: 'var(--text)', fontFamily: 'var(--mono)' }}>{name}</strong> created.
              Save these credentials - they will <strong style={{ color: 'var(--amber)' }}>never be shown again</strong>.
            </div>
            {secret.client_id && (
              <div style={{ marginBottom: 12 }}>
                <div className="field-label">Client ID</div>
                <CopyBlock value={secret.client_id} />
              </div>
            )}
            <div>
              <div className="field-label">Client Secret</div>
              <CopyBlock value={secret.client_secret} />
            </div>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.6, marginBottom: 10 }}>
            Hand both to the Terramantle CLI and it does the grant for you - it picks up{' '}
            <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>client</code> auth mode on its own:
          </div>
          <div style={{ marginBottom: 16 }}>
            <CopyBlock value={cliBotEnv} />
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text3)', marginBottom: 16, lineHeight: 1.5 }}>
            Doing the exchange yourself instead? Use <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>grant_type=client_credentials</code> against the IdP token endpoint. In the grant, the fixed <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>client_id</code> is <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>terramantle-bots</code>; the Client ID above goes in <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>username</code> and the Client Secret in <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>password</code>.
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.6, marginBottom: 20 }}>
            Now you have a bot token, learn how to wire it into your pipeline -{' '}
            <a
              href="/integrations/cli-ci"
              target="_blank"
              rel="noopener noreferrer"
              style={s.link}
            >
              using the CLI in CI →
            </a>
            {' '}· <a
              href="/integrations/using-providers"
              target="_blank"
              rel="noopener noreferrer"
              style={s.link}
            >
              consuming providers →
            </a>
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.5, marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={confirmedSaved}
              onChange={(e) => setConfirmedSaved(e.target.checked)}
              style={{ marginTop: 2, flexShrink: 0 }}
            />
            <span>I've saved the client secret somewhere safe.</span>
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary btn-sm" onClick={onClose} disabled={!confirmedSaved}>
              Close
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
          <div>
            <label className="field-label">Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })) }}
              autoFocus
            />
            {errors.name && <div style={s.fieldError}>{errors.name}</div>}
          </div>

          <div>
            <label className="field-label">Description <span className="dim">(optional)</span></label>
            <input
              className="input"
              placeholder="e.g. github-actions, gitlab-ci"
              value={description}
              onChange={(e) => { setDesc(e.target.value); setErrors((p) => ({ ...p, description: undefined })) }}
            />
            {errors.description && <div style={s.fieldError}>{errors.description}</div>}
          </div>

          <div>
            <div className="field-label" style={{ marginBottom: 8 }}>Access scope</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>

              {/* Global */}
              <ScopeRow
                checked={isGlobal}
                label="Global"
                desc="full access to all modules and state in this org"
                onChange={handleGlobalToggle}
                type="radio"
              />

              {/* Org modules - coming soon */}
              <ScopeRow
                checked={orgEnabled}
                label="Org modules"
                desc="read/write access to modules in this org"
                onChange={handleOrgToggle}
                type="checkbox"
                disabled
              />

              {/* State workspaces - coming soon */}
              <ScopeRow
                checked={stateEnabled}
                label="State workspaces"
                desc="access to specific state workspaces"
                onChange={handleStateToggle}
                type="checkbox"
                disabled
              />
              {stateEnabled && (
                <div style={{ padding: '0 12px 10px 36px', background: 'var(--bg3)' }}>
                  <StateGrantList orgId={orgId} grants={stateGrants} onToggle={toggleStateGrant} onPermChange={setStatePerm} />
                </div>
              )}

            </div>
            {errors.grants && <div style={s.fieldError}>{errors.grants}</div>}
          </div>

          {apiError && <div className="feedback-error">{apiError}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? <span className="spinner" /> : 'Create token'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

function ScopeRow({ checked, label, desc, onChange, type, disabled }: {
  checked: boolean
  label: string
  desc: string
  onChange: () => void
  type: 'radio' | 'checkbox'
  disabled?: boolean
}) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      padding: '10px 12px',
      background: checked ? 'var(--accent-d)' : 'var(--bg2)',
      borderBottom: '1px solid var(--border)',
    }}>
      <input
        type={type}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{ marginTop: 3, flexShrink: 0 }}
      />
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', color: 'var(--text)', fontWeight: 500 }}>
          {label}
          {disabled && (
            <span style={{
              fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
              fontFamily: 'var(--mono)', color: 'var(--text3)',
              border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px',
            }}>
              Coming soon
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{desc}</div>
      </div>
    </label>
  )
}

function GrantList({ items, grants, onToggle, onPermChange, emptyMsg, loading }: {
  items: Array<{ id: string; label: string }>
  grants: Map<string, Perm>
  onToggle: (id: string) => void
  onPermChange: (id: string, p: Perm) => void
  emptyMsg: string
  loading?: boolean
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 6 }}>
      {loading && <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>Loading…</div>}
      {!loading && items.length === 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>{emptyMsg}</div>}
      {items.map((item) => (
        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
          <input type="checkbox" checked={grants.has(item.id)} onChange={() => onToggle(item.id)} />
          <span style={{ flex: 1, fontSize: '0.8rem', fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{item.label}</span>
          {grants.has(item.id) && (
            <PermSelect value={grants.get(item.id)!} onChange={(p) => onPermChange(item.id, p)} noNone />
          )}
        </div>
      ))}
    </div>
  )
}

function StateGrantList({ orgId, grants, onToggle, onPermChange }: {
  orgId: string
  grants: Map<string, Perm>
  onToggle: (id: string) => void
  onPermChange: (id: string, p: Perm) => void
}) {
  const workspaces = useStateWorkspaces(orgId)
  return (
    <GrantList
      items={(workspaces.data?.workspaces ?? []).map((ws) => ({ id: ws.id, label: ws.name }))}
      grants={grants}
      onToggle={onToggle}
      onPermChange={onPermChange}
      emptyMsg="No workspaces in this org."
      loading={workspaces.loading}
    />
  )
}

function PermSelect({ value, onChange, noNone }: { value: Perm; onChange: (p: Perm) => void; noNone?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {(!noNone ? (['none', 'read', 'write'] as Perm[]) : (['read', 'write'] as Perm[])).map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          style={{
            padding: '2px 10px', borderRadius: 4, border: '1px solid',
            fontSize: '0.72rem', fontFamily: 'var(--mono)', cursor: 'pointer',
            borderColor: value === p ? 'var(--accent)' : 'var(--border)',
            background: value === p ? 'var(--accent-d)' : 'transparent',
            color: value === p ? 'var(--accent)' : 'var(--text3)',
          }}
        >
          {p}
        </button>
      ))}
    </div>
  )
}


const s: Record<string, React.CSSProperties> = {
  fieldError: { marginTop: 4, fontSize: '0.72rem', color: 'var(--red)' },
  link: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    color: 'var(--accent)', fontFamily: 'inherit', fontSize: 'inherit',
    textDecoration: 'underline',
  },
}
